import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, Send, MailCheck, Reply, PauseCircle, AlertTriangle, UserPlus, TrendingUp, Clock, ShieldAlert, XCircle, Radio } from "lucide-react";
import { API } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { dateStyle: "medium" }) : "—");
const fmtWhen = (s) => (s ? new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "never");

const TONES = {
  slate: ["border-slate-200", "text-slate-600", "border-slate-200 bg-slate-50 text-slate-800"],
  blue: ["border-blue-200", "text-blue-600", ""],
  emerald: ["border-emerald-200", "text-emerald-600", "border-emerald-200 bg-emerald-50 text-emerald-800"],
  rose: ["border-rose-200", "text-rose-600", "border-rose-200 bg-rose-50 text-rose-800"],
  orange: ["border-orange-200", "text-orange-600", ""],
  amber: ["border-amber-200", "text-amber-600", "border-amber-200 bg-amber-50 text-amber-800"],
  teal: ["border-teal-200", "text-teal-600", ""],
  indigo: ["border-indigo-200", "text-indigo-600", ""],
  violet: ["border-violet-200", "text-violet-600", ""],
};

const Metric = ({ icon: Icon, label, value, tone = "slate", testid, hint }) => (
  <div className={`rounded-xl border bg-white p-4 flex flex-col gap-1 ${TONES[tone][0]}`} data-testid={testid} title={hint || ""}>
    <div className={`flex items-center gap-1.5 ${TONES[tone][1]}`}>
      <Icon className="w-4 h-4" />
      <span className="text-xs font-medium text-slate-500">{label}</span>
    </div>
    <span className="text-2xl font-bold text-slate-900 tabular-nums">{value}</span>
  </div>
);

function WebhookHealth({ w }) {
  if (!w) return null;
  const tone = w.stale ? "rose" : (w.last_event_at ? "emerald" : "amber");
  const msg = w.stale
    ? "Emails are being sent but SendGrid has not reported delivery events since the last send. Delivered / bounced / opened will stay frozen until the Event Webhook is configured."
    : (w.last_event_at ? "SendGrid is reporting delivery events." : "No delivery events received yet. Configure the SendGrid Event Webhook to track delivered, bounced, blocked, opened and clicked.");
  return (
    <div className={`rounded-xl border p-3 text-xs flex flex-wrap items-center gap-x-4 gap-y-1 ${TONES[tone][2]}`} data-testid="webhook-health" data-state={w.stale ? "stale" : (w.last_event_at ? "ok" : "never")}>
      <span className="flex items-center gap-1.5 font-medium"><Radio className="w-3.5 h-3.5" /> SendGrid event feed</span>
      <span>Last event: <strong data-testid="webhook-last-event">{fmtWhen(w.last_event_at)}</strong>{w.last_event_type ? ` (${w.last_event_type})` : ""}</span>
      <span>Last send: <strong>{fmtWhen(w.last_send_at)}</strong></span>
      <span className="basis-full">{msg} Endpoint: <code className="bg-white/70 px-1 rounded">{window.location.origin}{w.endpoint}</code></span>
    </div>
  );
}

export default function BespokeCampaignAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/campaigns/bespoke/analytics`, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      toast.error(`Failed to load insights (${e.message})`);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>;
  if (!data) return null;

  const o = data.overall || {};
  return (
    <div className="space-y-5 pr-6" data-testid="bespoke-analytics">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-slate-500 flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Across {o.campaigns || 0} campaign(s)</h3>
        <Button size="sm" variant="outline" onClick={load} data-testid="analytics-refresh"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
      </div>

      <WebhookHealth w={data.webhook} />

      {data.data_quality && data.data_quality.unreliable_window && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="analytics-data-quality">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold mb-0.5">Some delivery figures below are unreliable for a bounded period</div>
              <div>{data.data_quality.note}</div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Metric icon={Send} label="Sent" value={o.sent || 0} tone="blue" testid="metric-sent" hint="Genuine deliveries only. Redirected/suppressed sends are counted separately and are NOT included here." />
        <Metric icon={MailCheck} label={`Delivered (${o.delivery_rate || 0}%)`} value={o.delivered || 0} tone="emerald" testid="metric-delivered" />
        <Metric icon={Clock} label="Awaiting delivery report" value={o.awaiting_delivery || 0} tone="slate" testid="metric-awaiting" hint="Sent, but SendGrid has not confirmed delivery yet" />
        <Metric icon={AlertTriangle} label={`Bounced (${o.bounce_rate || 0}%)`} value={o.bounced || 0} tone="rose" testid="metric-bounced" />
        <Metric icon={ShieldAlert} label="Blocked" value={o.blocked || 0} tone="orange" testid="metric-blocked" hint="Receiving server refused the message (reputation/policy). Address is still valid." />
        <Metric icon={XCircle} label="Dropped / failed" value={(o.dropped || 0) + (o.failed || 0)} tone="rose" testid="metric-dropped" hint="Dropped by SendGrid (suppression) or failed at send" />
        <Metric icon={Clock} label="Deferred" value={o.deferred || 0} tone="amber" testid="metric-deferred" hint="Receiving server asked SendGrid to retry later" />
        {(o.redirected || 0) > 0 && (
          <Metric icon={ShieldAlert} label="Redirected (not delivered)" value={o.redirected} tone="orange" testid="metric-redirected" hint="Non-production: diverted to an internal allowlist. The intended recipient did NOT receive it." />
        )}
        {(o.suppressed || 0) > 0 && (
          <Metric icon={ShieldAlert} label="Suppressed (not sent)" value={o.suppressed} tone="orange" testid="metric-suppressed" hint="Non-production with an empty allowlist: nothing was sent anywhere." />
        )}
        <Metric icon={Reply} label={`Replied (${o.reply_rate || 0}%)`} value={o.replied || 0} tone="teal" testid="metric-replied" />
        <Metric icon={MailCheck} label="Opened" value={o.opened || 0} tone="indigo" testid="metric-opened" />
        <Metric icon={PauseCircle} label="On hold (OOO)" value={o.on_hold || 0} tone="amber" testid="metric-onhold" />
        <Metric icon={AlertTriangle} label="Unsubscribed / spam" value={(o.unsubscribed || 0) + (o.spam_reported || 0)} tone="rose" testid="metric-unsub" />
        <Metric icon={UserPlus} label="Warm referrals" value={o.delegates || 0} tone="indigo" testid="metric-delegates" />
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="analytics-table">
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b bg-slate-50">
                <th className="px-3 py-2">Campaign</th>
                <th className="px-3 py-2 text-right">Sent</th>
                <th className="px-3 py-2 text-right">Delivered</th>
                <th className="px-3 py-2 text-right">Bounced</th>
                <th className="px-3 py-2 text-right">Blocked</th>
                <th className="px-3 py-2 text-right">Dropped/failed</th>
                <th className="px-3 py-2 text-right">Opened</th>
                <th className="px-3 py-2 text-right">Replied</th>
                <th className="px-3 py-2 text-right">Reply&nbsp;%</th>
                <th className="px-3 py-2 text-right">On&nbsp;hold</th>
                <th className="px-3 py-2 text-right">Nudges</th>
              </tr>
            </thead>
            <tbody>
              {(data.campaigns || []).map((c) => (
                <tr key={c.campaign_id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`analytics-row-${c.campaign_id}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-800 truncate max-w-[260px]">{c.name}</div>
                    <div className="text-xs text-slate-400">{fmtDate(c.created_at)} · {c.sender_name} · {c.status}</div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{c.sent}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{c.delivered}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-rose-600">{c.bounced}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-orange-600">{c.blocked}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-rose-600">{(c.dropped || 0) + (c.failed || 0)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{c.opened}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{c.replied}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-teal-700">{c.reply_rate}%</td>
                  <td className="px-3 py-2 text-right tabular-nums text-amber-700">{c.on_hold}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{c.nudges_sent}</td>
                </tr>
              ))}
              {(data.campaigns || []).length === 0 && (
                <tr><td colSpan={11} className="px-3 py-10 text-center text-slate-400">No campaign data yet.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
